import Image from "next/image";
import { Play } from "lucide-react";
// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, so
// every card click paid a middleware redirect round-trip.
import { Link } from "@/i18n/routing";
import type { ReactNode } from "react";
import type { LjArticle } from "@/fixtures/lj/fixtures";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

interface Props {
  article: LjArticle;
  /** Headline scale. */
  size?: "xl" | "lg" | "sm";
  /** Image aspect ratio. Defaults to 4/3. */
  aspect?: number;
  /** Optional dek shown beneath the headline. */
  dek?: string;
  /** Optional overlay rendered on top of the image (e.g. play button). */
  imageOverlay?: ReactNode;
  className?: string;
}

const HED_STYLE = {
  xl: { fontSize: 36, lineHeight: "44px" },
  lg: LJ_TYPE.cardHed,
  sm: LJ_TYPE.cardHedSm,
} as const;

export function ArticleCard({
  article,
  size = "lg",
  aspect = 4 / 3,
  dek,
  imageOverlay,
  className,
}: Props) {
  const isVideo =
    "duration" in article && typeof (article as { duration?: unknown }).duration === "string";

  const hedStyle =
    size === "xl"
      ? { ...LJ_TYPE.heroHed, ...HED_STYLE.xl }
      : HED_STYLE[size];

  return (
    <Link
      href={article.href}
      className={`group hkej-tap flex flex-col gap-4 ${className ?? ""}`}
      style={{ color: LJ_COLORS.textPrimary }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: aspect,
          backgroundColor: LJ_COLORS.bgSubtle,
        }}
      >
        <Image
          src={article.image}
          alt={article.title}
          fill
          className="object-contain object-center"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 480px"
          unoptimized
        />
        {/* A film says so with the play mark alone — the running time was a
            second badge on the same picture and QA asked for it off. Driven by
            the DATA, so the video listing can use the same layout as every
            other category page and still read as video. */}
        {isVideo ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-3 left-3 grid h-12 w-12 place-items-center rounded-full backdrop-blur-sm"
            style={{ backgroundColor: LJ_COLORS.playButtonBg }}
          >
            <Play
              className="h-5 w-5 translate-x-[1px]"
              style={{ color: LJ_COLORS.textPrimary }}
              fill={LJ_COLORS.textPrimary}
              strokeWidth={0}
            />
          </span>
        ) : null}
        {imageOverlay}
      </div>

      <div className="flex flex-col items-start text-left">
        <span
          style={{
            ...LJ_TYPE.eyebrow,
            fontWeight: 400,
            color: LJ_COLORS.textPrimary,
          }}
        >
          {article.category}
        </span>
        <h3
          className="hkej-tap-title line-clamp-2"
          style={{
            ...hedStyle,
            color: LJ_COLORS.textPrimary,
            marginTop: 6,
          }}
        >
          {article.title}
        </h3>
        {dek ? (
          <p
            className="line-clamp-2"
            style={{
              ...LJ_TYPE.dek,
              color: LJ_COLORS.textBody,
              marginTop: 6,
            }}
          >
            {dek}
          </p>
        ) : null}
        {article.author ? (
          <span
            style={{
              ...LJ_TYPE.byline,
              fontWeight: 400,
              color: LJ_COLORS.textPrimary,
              marginTop: 16,
            }}
          >
            By {article.author}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
